'use client'

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Loader2, Plus, Pencil, Server, Users, CheckCircle, XCircle } from 'lucide-react'
import {
  ElevenLabsAccountDialog,
  type ElevenLabsAccountData,
} from './elevenlabs-account-dialog'
import { TenantAssignmentsDialog } from './tenant-assignments-dialog'

interface PoolStatus {
  totalAccounts: number
  activeAccounts: number
  totalConcurrency: number
  usedConcurrency: number
  availableConcurrency: number
  totalAgents: number
  usedAgents: number
  availableAgents: number
  realAgents: number
  utilizationPercent: number
}

interface ElevenLabsAccount extends ElevenLabsAccountData {
  api_key_masked: string | null
  status: string
  tenant_count: number
  used_concurrency: number
  used_agents: number
  available_concurrency: number
  available_agents: number
  real_agents: number
}

/** Convert microUSD to display dollars (e.g. 80000 → "0.08") */
function microToDisplay(micro: number): string {
  return (micro / 1_000_000).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
}

/**
 * Red when reserved usage exceeds capacity. Reserved slots are "soft" (allowed
 * to exceed max), so over-capacity is highlighted, not blocked.
 */
function overReservedClass(used: number, max: number): string {
  return used > max ? 'text-destructive' : ''
}

/**
 * Red when REAL agents reach/exceed capacity — the hard create-limit. At this
 * point new voice agents are blocked on this account.
 */
function atRealCapacityClass(real: number, max: number): string {
  return real >= max ? 'text-destructive' : ''
}

export function ElevenLabsPool() {
  const { fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const [pool, setPool] = useState<PoolStatus | null>(null)
  const [accounts, setAccounts] = useState<ElevenLabsAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogAccount, setDialogAccount] = useState<ElevenLabsAccountData | null>(null)

  // Assignments dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignAccountId, setAssignAccountId] = useState<string | null>(null)
  const [assignAccountName, setAssignAccountName] = useState('')

  const canManagePool = can('billing.manage_elevenlabs_pool')

  const fetchData = useCallback(async () => {
    try {
      const [poolRes, accountsRes] = await Promise.all([
        fetchWithAuth('/api/admin/billing/elevenlabs/pool'),
        fetchWithAuth('/api/admin/billing/elevenlabs/accounts'),
      ])

      if (!poolRes.ok || !accountsRes.ok) {
        throw new Error('Failed to fetch pool data')
      }

      const poolData = await poolRes.json()
      const accountsData = await accountsRes.json()

      setPool(poolData.pool)
      setAccounts(accountsData.accounts)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleAdd = () => {
    setDialogAccount(null)
    setDialogOpen(true)
  }

  const handleEdit = (account: ElevenLabsAccount) => {
    setDialogAccount(account)
    setDialogOpen(true)
  }

  const handleManageAssignments = (account: ElevenLabsAccount) => {
    setAssignAccountId(account.id!)
    setAssignAccountName(account.name)
    setAssignDialogOpen(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-destructive">
        <span>Error loading pool:</span> {error}
        <Button variant="link" onClick={() => setError(null)}>
          Dismiss
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Voice Provider Pool</h2>
          <p className="text-muted-foreground">Manage ElevenLabs account capacity</p>
        </div>
        {canManagePool && (
          <Button onClick={handleAdd}>
            <Plus className="mr-2 h-4 w-4" />
            <span>Add Account</span>
          </Button>
        )}
      </div>

      {/* Pool Summary */}
      {pool && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Utilization</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pool.utilizationPercent}<span>%</span></div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 bg-secondary rounded-full h-2">
                  <div
                    className={`rounded-full h-2 ${
                      pool.utilizationPercent > 80 ? 'bg-destructive' : 'bg-primary'
                    }`}
                    style={{ width: `${pool.utilizationPercent}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Server className="h-4 w-4" />
                <span>Concurrency</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${overReservedClass(pool.usedConcurrency, pool.totalConcurrency)}`}>
                {pool.usedConcurrency} <span>/</span> {pool.totalConcurrency}
              </div>
              <p className="text-xs text-muted-foreground">
                {pool.availableConcurrency} <span>available (reserved)</span>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Agents</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${atRealCapacityClass(pool.realAgents, pool.totalAgents)}`}>
                {pool.realAgents} <span>/</span> {pool.totalAgents}
              </div>
              <p className="text-xs text-muted-foreground">real agents created</p>
              <p className={`mt-1 text-xs text-muted-foreground ${overReservedClass(pool.usedAgents, pool.totalAgents)}`}>
                {pool.usedAgents} <span>/</span> {pool.totalAgents} <span>reserved</span>
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Accounts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Accounts</CardTitle>
          <CardDescription>ElevenLabs accounts in the pool</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Cost/min</TableHead>
                <TableHead>Webhook</TableHead>
                <TableHead>Concurrency</TableHead>
                <TableHead>Agents</TableHead>
                <TableHead>Tenants</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    No accounts configured
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>
                      <div className="font-medium">{account.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {account.api_key_masked}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          account.status === 'active'
                            ? 'default'
                            : account.status === 'full'
                              ? 'secondary'
                              : 'destructive'
                        }
                      >
                        {account.status}
                      </Badge>
                      {account.is_default && (
                        <Badge variant="outline" className="ml-1">
                          default
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        ${microToDisplay(account.cost_per_minute_microusd)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {account.webhook_id ? (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                          {account.webhook_name && (
                            <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                              {account.webhook_name}
                            </span>
                          )}
                        </div>
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className={overReservedClass(account.used_concurrency, account.max_concurrency)}>
                      {account.used_concurrency} <span>/</span> {account.max_concurrency}
                    </TableCell>
                    <TableCell>
                      <div className={atRealCapacityClass(account.real_agents, account.max_agents)}>
                        {account.real_agents} <span>/</span> {account.max_agents}
                      </div>
                      <div className={`text-xs text-muted-foreground ${overReservedClass(account.used_agents, account.max_agents)}`}>
                        {account.used_agents} <span>/</span> {account.max_agents} <span>reserved</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {canManagePool ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 font-normal hover:underline"
                          onClick={() => handleManageAssignments(account)}
                        >
                          <Users className="mr-1 h-3 w-3" />
                          {account.tenant_count}
                        </Button>
                      ) : (
                        account.tenant_count
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManagePool && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(account)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ElevenLabsAccountDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        account={dialogAccount}
        onSaved={fetchData}
      />

      <TenantAssignmentsDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        accountId={assignAccountId}
        accountName={assignAccountName}
        onChanged={fetchData}
      />
    </div>
  )
}
